/**
 * 아주 작은 이벤트 에미터. 의존을 추가하지 않으려고 여기 있다 —
 * 구독 등록/해제와 fan-out 말고는 아무것도 하지 않는다.
 *
 * 왜 `EventTarget` 을 안 쓰는가: 페이로드가 `CustomEvent.detail` 안의 `any` 가 되고,
 * 그 순간 "401 인지 스큐인지"를 타입이 말해 주지 못한다. 이 파일의 존재 이유가
 * 그 판정을 타입으로 남기는 것이라 20줄을 직접 적는 쪽을 골랐다.
 */
export type Unsubscribe = () => void;

export interface Emitter<TMap> {
  on<K extends keyof TMap>(event: K, handler: (payload: TMap[K]) => void): Unsubscribe;
  emit<K extends keyof TMap>(event: K, payload: TMap[K]): void;
}

export interface EmitterOptions {
  /**
   * 구독자 하나가 던졌을 때. **기본은 마이크로태스크로 다시 던지는 것**이다 —
   * 그러면 발행자(RPC 통로)는 안 죽고, 에러는 `window.onerror` 까지 올라가 조용히 사라지지 않는다.
   * 삼키지 않는 이유는 AGENTS.md 15 그대로다: 숨긴 에러는 없는 에러가 된다.
   */
  readonly onHandlerError?: (err: unknown, event: PropertyKey) => void;
}

export function createEmitter<TMap>(opts: EmitterOptions = {}): Emitter<TMap> {
  const handlers = new Map<keyof TMap, Set<(payload: never) => void>>();
  const onHandlerError =
    opts.onHandlerError ??
    ((err) => {
      queueMicrotask(() => {
        throw err;
      });
    });
  return {
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      set.add(handler as (payload: never) => void);
      return () => {
        set.delete(handler as (payload: never) => void);
      };
    },
    emit(event, payload) {
      /*
       * 사본 위에서 돌고, 던진 구독자를 **건너뛴다.**
       * 한 구독자의 버그가 나머지 배달을 멈추면 그 상태는 조용하다 —
       * 401 구독자가 죽어서 스큐 배너가 영원히 안 뜨는 것이 정확히 그 형태다.
       */
      for (const h of [...(handlers.get(event) ?? [])]) {
        try {
          (h as (p: TMap[typeof event]) => void)(payload);
        } catch (err) {
          onHandlerError(err, event as PropertyKey);
        }
      }
    },
  };
}
